import { Box, Flex, Image, useToast } from "@chakra-ui/react";
import { useSignal } from "@preact/signals";
import type React from "react";
import { type FormEvent, Fragment, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  formContainsData,
  getMultiselectResponse,
  getTextValue,
} from "../../../../lib/questionnaire/formFunctions";
import PencilIcon from "../../../assets/icons/pencil.svg";
import { IncidentLogDisclaimer } from "../../../components/modals/pdfDisclaimer";
import { globalState } from "../../../state";
import Button from "../../components/Button";
import ButtonLink from "../../components/ButtonLink";
import HeaderText from "../../components/HeaderText";
import { IncidentLogBanner } from "../../components/IncidentLogBanner";
import AnimatedBox from "../../components/layout/AnimatedBox";
import WhiteButton from "../../components/layout/WhiteButton";
import MainCard from "../../components/MainCard";
import { PrivateRoutes } from "../../config/routes";
import { IncidentLogContext } from "../../contexts/IncidentLogContext";
import { wait } from "../../service/backend";
import ReviewSubHeader from "../OrfReview/ReviewSubHeader";

export const IncidentLogSummaryPage: React.FC = () => {
  const {
    state: { mode, currentLogId, steps, results },
    setSavingScreenVisibility,
    saveIncidentLog,
  } = useContext(IncidentLogContext);
  const showDisclaimerModal = useSignal(false);
  const navigate = useNavigate();
  const toast = useToast();

  const handleSubmit = async (e: FormEvent<HTMLDivElement>) => {
    e.preventDefault();

    setSavingScreenVisibility(true);
    try {
      if (formContainsData(results)) {
        await wait(1000);
        await saveIncidentLog(mode, currentLogId);
      } else {
        toast({
          position: "top",
          status: "error",
          title:
            "Please answer at least one question. We cannot save an empty Incident Log.",
        });
      }
    } finally {
      setSavingScreenVisibility(false);
    }
  };

  return (
    <AnimatedBox>
      <IncidentLogBanner />
      <MainCard>
        <Box as="form" onSubmit={handleSubmit} id="incident_log_form">
          <WhiteButton
            m="3px"
            className="rightAligned"
            onClick={() => {
              globalState.client
                .submitEvent("Button Click", {
                  feature: `Incident Log PDF Download`,
                  button: "Download Incident Log as PDF",
                })
                .finally(() => {
                  showDisclaimerModal.value = true;
                });
            }}
          >
            Download as PDF
          </WhiteButton>
          <HeaderText>Summary</HeaderText>
          {Object.values(steps).map((step) => {
            if (step.questions.length > 0) {
              return (
                <Fragment key={`${step.text}mainfragment`}>
                  <ReviewSubHeader key={step.text}>
                    {step.text}
                    {mode === "edit" && (
                      <Link to={step.route}>
                        <ButtonLink color="brand.link">
                          <Image src={PencilIcon as string} />
                        </ButtonLink>
                      </Link>
                    )}
                  </ReviewSubHeader>
                  {step.questions.map((question) => (
                    <Fragment key={`${question.key}frag`}>
                      <p key={question.key}>
                        <b key={`${question.key}b`}>{question.label}</b>
                      </p>
                      {(question.type === "text" ||
                        question.type === "number" ||
                        question.type === "textarea") && (
                        <p key={`${question.key}answer`} className="answer">
                          {getTextValue([question.key], results)}
                        </p>
                      )}
                      {question.type === "multiselect" && (
                        <div key={`${question.key}answer`} className="answer">
                          <ul className="ul" key={`${question.key}ul`}>
                            {getMultiselectResponse(
                              [question.key],
                              results,
                            ).map((resp) => {
                              if (resp.label.toLowerCase() === "other") {
                                return (
                                  <li key={resp.key}>Other: {resp.value}</li>
                                );
                              }
                              return <li key={resp.key}>{resp.label}</li>;
                            })}
                          </ul>
                        </div>
                      )}
                    </Fragment>
                  ))}
                </Fragment>
              );
            }

            return <></>;
          })}
          <Flex flexDir="column" mt="60px">
            <Button
              buttonColor="brand.primary"
              type="submit"
              marginBottom="20px"
            >
              Save & Exit
            </Button>
            <ButtonLink
              onClick={() => {
                if (steps.summary.previousStepRoute) {
                  navigate(steps.summary.previousStepRoute);
                }
              }}
            >
              Back
            </ButtonLink>
          </Flex>
        </Box>
      </MainCard>
      <IncidentLogDisclaimer
        open={showDisclaimerModal}
        onCancel={() => {}}
        onComplete={(legal: boolean) => {
          globalState.incidentLogForAttorney.value = legal;
          navigate(PrivateRoutes.ILOG_PDF_DOWNLOAD);
        }}
      />
    </AnimatedBox>
  );
};
